import React from 'react'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import { alpha } from '@mui/material/styles'
import { TaskCard, Task } from '@/components/tasks/TaskCard'

export interface Status {
  key: string
  name: string
  color: string
}

interface Props {
  status: Status
  tasks: Task[]
  onTaskClick: (task: Task) => void
  showBoardProject?: boolean
  seamless?: boolean
  isCollapsed?: boolean
  onToggleCollapse?: () => void
}

export function KanbanColumn({ status, tasks, onTaskClick, showBoardProject = false, seamless = false, isCollapsed = false, onToggleCollapse }: Props) {
  if (isCollapsed) {
    return (
      <Paper
        elevation={0}
        onClick={onToggleCollapse}
        sx={{
          width: 44,
          minWidth: 44,
          maxWidth: 44,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: alpha(status.color, 0.10),
          borderRight: seamless ? '1px solid' : undefined,
          borderColor: 'divider',
          borderRadius: seamless ? 0 : 2,
          height: '100%',
          cursor: 'pointer',
          overflow: 'hidden',
          transition: 'width 0.2s ease, min-width 0.2s ease, max-width 0.2s ease',
          '&:hover': {
            backgroundColor: alpha(status.color, 0.18),
          },
        }}
      >
        <Box sx={{ height: '4px', width: '100%', flexShrink: 0, backgroundColor: status.color }} />
        <Tooltip title={`Expand ${status.name}`} placement="right">
          <IconButton size="small" sx={{ mt: 0.5, color: status.color }} onClick={onToggleCollapse}>
            <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1.5, gap: 1 }}>
          <Typography
            variant="caption"
            fontWeight={600}
            sx={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              color: status.color,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              userSelect: 'none',
            }}
          >
            {status.name}
          </Typography>
          <Chip
            label={tasks.length}
            size="small"
            sx={{
              height: 22,
              minWidth: 22,
              borderRadius: '11px',
              backgroundColor: alpha(status.color, 0.15),
              color: status.color,
              fontWeight: 700,
              fontSize: '0.7rem',
              '& .MuiChip-label': { px: '5px', lineHeight: 1 },
            }}
          />
        </Box>
      </Paper>
    )
  }

  return (
    <Paper
      elevation={0}
      sx={seamless ? {
        minWidth: 280,
        flex: '1 0 280px',
        borderRight: '1px solid',
        borderColor: 'divider',
        borderRadius: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        transition: 'width 0.2s ease, min-width 0.2s ease, flex 0.2s ease',
        '&:last-of-type': { borderRight: 'none' },
      } : {
        minWidth: { xs: 280, sm: 300 },
        maxWidth: { xs: 280, sm: 300 },
        flexShrink: 0,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: { xs: 'calc(100vh - 240px)', md: 'calc(100vh - 220px)' },
        transition: 'width 0.2s ease, min-width 0.2s ease',
      }}
    >
      <Box sx={{ height: '4px', flexShrink: 0, backgroundColor: status.color, ...(!seamless && { borderRadius: '8px 8px 0 0' }) }} />
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" fontWeight={600}>
          {status.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Chip label={tasks.length} size="small" variant="outlined" sx={{ '& .MuiChip-label': { px: '6px', lineHeight: 1 } }} />
          {onToggleCollapse && (
            <Tooltip title={`Collapse ${status.name}`}>
              <IconButton size="small" onClick={onToggleCollapse} sx={{ ml: 0.5 }}>
                <KeyboardArrowLeftIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      <Box sx={{ px: 1, pb: 1, overflowY: 'auto', flexGrow: 1 }}>
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            onClick={() => onTaskClick(task)}
            {...(showBoardProject && (task as any).board ? {
              board: { name: (task as any).board.name, color: (task as any).board.color },
              project: { name: (task as any).project.name, color: (task as any).project.color },
            } : {})}
          />
        ))}
        {tasks.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            No tasks
          </Typography>
        )}
      </Box>
    </Paper>
  )
}
